import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';

// Login se importa de forma normal: es lo primero que ve todo el mundo, así que
// diferirlo solo agregaría un parpadeo.
import Login from './pages/Login';

// El resto se carga bajo demanda. Antes las cinco páginas viajaban en un único archivo
// de ~1 MB que el administrador tenía que descargar completo antes de ver nada; en una
// conexión lenta eso son entre 15 y 25 segundos de pantalla en blanco.
// Con esto, cada página llega solo cuando se entra a ella. En particular recharts —la
// librería más pesada después de Firebase— queda confinada al Dashboard.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Admins = lazy(() => import('./pages/Admins'));
const AdminHistory = lazy(() => import('./pages/AdminHistory'));
const Newsletter = lazy(() => import('./pages/Newsletter'));
const Mensajeria = lazy(() => import('./pages/Mensajeria'));

function CargandoPagina() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary-500" />
    </div>
  );
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isSuperAdmin, loading } = useAuth();
  
  if (loading) return null;
  if (!user || !isSuperAdmin) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

function AppRoutes() {
  const { user, isSuperAdmin } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user && isSuperAdmin ? <Navigate to="/" replace /> : <Login />} 
      />
      
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Suspense fallback={<CargandoPagina />}><Dashboard /></Suspense>} />
        <Route path="admins" element={<Suspense fallback={<CargandoPagina />}><Admins /></Suspense>} />
        <Route path="admins/:id/history" element={<Suspense fallback={<CargandoPagina />}><AdminHistory /></Suspense>} />
        <Route path="remarketing" element={<Navigate to="/remarketing/newsletter" replace />} />
        <Route path="remarketing/newsletter" element={<Suspense fallback={<CargandoPagina />}><Newsletter /></Suspense>} />
        <Route path="remarketing/mensajeria" element={<Suspense fallback={<CargandoPagina />}><Mensajeria /></Suspense>} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
